export type SocialPlatform = 'facebook' | 'instagram' | 'twitter' | 'youtube' | 'linkedin';

export interface SocialLinkItem {
  url: string;
  enabled: boolean;
}

export type SocialLinks = Record<SocialPlatform, SocialLinkItem>;

export const SOCIAL_PLATFORM_CONFIG: Record<
  SocialPlatform,
  { label: string; placeholder: string }
> = {
  facebook: {
    label: 'Facebook',
    placeholder: 'https://facebook.com/yourpage',
  },
  instagram: {
    label: 'Instagram',
    placeholder: 'https://instagram.com/yourpage',
  },
  twitter: {
    label: 'Twitter / X',
    placeholder: 'https://twitter.com/yourpage',
  },
  youtube: {
    label: 'YouTube',
    placeholder: 'https://youtube.com/@yourchannel',
  },
  linkedin: {
    label: 'LinkedIn',
    placeholder: 'https://linkedin.com/company/yourcompany',
  },
};

export const createDefaultSocialLinks = (): SocialLinks => ({
  facebook: { url: '', enabled: false },
  instagram: { url: '', enabled: false },
  twitter: { url: '', enabled: false },
  youtube: { url: '', enabled: false },
  linkedin: { url: '', enabled: false },
});

type LegacySocialUrlMap = Partial<Record<`${SocialPlatform}Url`, string | undefined>>;

const getLegacyUrl = (legacyUrls: LegacySocialUrlMap | undefined, platform: SocialPlatform) =>
  legacyUrls?.[`${platform}Url`];

export const normalizeSocialLinks = (
  socialLinks?: Partial<Record<SocialPlatform, Partial<SocialLinkItem>>> | null,
  legacyUrls?: LegacySocialUrlMap
): SocialLinks => {
  const defaults = createDefaultSocialLinks();

  return (Object.keys(defaults) as SocialPlatform[]).reduce<SocialLinks>((acc, platform) => {
    const entry = socialLinks?.[platform];
    const legacyUrl = getLegacyUrl(legacyUrls, platform);
    const hasExplicitEntry = !!entry && (typeof entry.url === 'string' || typeof entry.enabled === 'boolean');

    if (hasExplicitEntry) {
      acc[platform] = {
        url: typeof entry?.url === 'string' ? entry.url : '',
        enabled: typeof entry?.enabled === 'boolean' ? entry.enabled : false,
      };
      return acc;
    }

    acc[platform] = {
      url: typeof legacyUrl === 'string' ? legacyUrl : '',
      enabled: Boolean(legacyUrl),
    };
    return acc;
  }, defaults);
};
